// @flow
import React, { type Element } from 'react';
import DonutChart from 'react-svg-donut-chart';
import { stylesColors as colors } from 'angieslist-styles';
import { Col, Row, OverlayTrigger, Popover } from 'angieslist-react-components';
import { urlCatalog } from 'angieslist-app-platform';
import { Link } from 'react-router';
import { AdvertiserOnlyPillComponent } from './AdvertiserOnlyPill';
import DealsModal from './DashboardV2/DealsModal';
import './CustomerContactsChart.scss';

type Props = {
  serviceProviderId: string,
  totalToDisplay: number,
  totalToDisplaySubtitle?: string,
  callsPercentage: number,
  trackedCallsPercentage: number,
  quoteRequestsPercentage: number,
  claimedDealsPercentage: number,
  strokeWidth?: number,
  isAdvertiser: boolean,
  name?: string,
  phoneNumber?: string,
};

const generateUID = () => performance.now().toFixed(0);

export const CustomerContactsChartComponent = ({
  totalToDisplay, totalToDisplaySubtitle, callsPercentage, trackedCallsPercentage,
  quoteRequestsPercentage, claimedDealsPercentage, strokeWidth,
  serviceProviderId, isAdvertiser, name, phoneNumber,
}: Props): Element<"div"> => {
  const data = [
    { value: Math.ceil(callsPercentage), stroke: colors.backgroundDark, strokeWidth },
    {
      value: Math.ceil(quoteRequestsPercentage), stroke: colors.backgroundHighlight2, strokeWidth,
    },
    { value: Math.ceil(claimedDealsPercentage), stroke: colors.iconLight, strokeWidth },
  ];

  const emptyData = [
    { value: 100, stroke: colors.gray, strokeWidth },
  ];

  const nonAdvertiserData = [
    {
      value: Math.ceil(quoteRequestsPercentage), stroke: colors.backgroundHighlight2, strokeWidth,
    },
  ];

  const roundedCallsPercentage = Math.round(callsPercentage);

  const calculationsPopover = (
    <Popover
      bsStyle="success"
      style={{ maxWidth: 450 }}
      id="customer-contacts--estimate-popover"
    >
      <span style={{ fontSize: '0.8em' }}>
        We can attribute most member phone calls to Angi Certified Pros due to our
        tracked phone numbers. However, some members contact Certified Pros outside our platform in
        a way we cannot directly track. Over a third of Angi members we sampled use the
        site to confirm recommendations from friends, neighbors, or family members, so they might
        already have your phone number. After a thorough analysis of our nationwide site traffic
        and reviews left without trackable contacts over the past year, we estimate on average how
        frequently this occurs. Of the calls metric shown, {Math.round(trackedCallsPercentage)}% are
        due to tracked phone calls. The remainder are from phone calls we believe happened off of
        our platform.
      </span>
    </Popover>
  );

  return (
    <div id="customer-contacts-chart">
      <Row>
        <Col xs={5} sm={4} md={5} className="donut-chart-wrapper">
          <div className="centered-chart-content">
            <span className="centered-chart-number">{totalToDisplay}</span>
            <If condition={totalToDisplaySubtitle}>
              <div className="centered-chart-description">{totalToDisplaySubtitle}</div>
            </If>
            <div className="centered-chart-description">Customer Contacts</div>
          </div>
          <div className="donut-chart">
            <Choose>
              <When condition={totalToDisplay === 0}>
                <DonutChart data={emptyData} />
              </When>
              <When condition={!isAdvertiser}>
                <DonutChart data={nonAdvertiserData} />
              </When>
              <When condition={isAdvertiser}>
                <DonutChart data={data} />
              </When>
            </Choose>
          </div>
        </Col>
        <Col xs={7} sm={7} md={7} className="donut-chart-legend">
          <ul>
            <li><span className="legend-dot calls-percentage" />
              <Choose>
                <When condition={isAdvertiser}>
                  {callsPercentage ?
                    <strong key={generateUID()}>{roundedCallsPercentage}&#x25; </strong> : '- '}
                  Calls (estimated)
                  <If condition={roundedCallsPercentage > 0}>
                    <OverlayTrigger
                      trigger="click"
                      placement="bottom"
                      overlay={calculationsPopover}
                      rootClose
                    >
                      <i
                        id="category-management-mobile-info-circle"
                        className="icon icon-help-circled"
                      />
                    </OverlayTrigger>
                  </If>
                </When>
                <Otherwise>
                  <span className="non-advertiser-title">
                    <strong key={generateUID()} >- &#x25;</strong> Calls (estimated)
                    <AdvertiserOnlyPillComponent customText="Advertiser Only" />
                  </span>
                </Otherwise>
              </Choose>
            </li>
            <li><span className="legend-dot quote-requests-percentage" />
              {quoteRequestsPercentage ? <strong key={generateUID()}>{Math.round(quoteRequestsPercentage)}&#x25; </strong> : '- '}
              Leads
            </li>
            <li><span className="legend-dot claimed-deals-percentage" />
              <Choose>
                <When condition={isAdvertiser}>
                  {claimedDealsPercentage ?
                    <strong key={generateUID()}>{Math.round(claimedDealsPercentage)}&#x25; </strong> : '- '} Claimed Deals
                </When>
                <Otherwise>
                  <span className="non-advertiser-title">
                    <strong key={generateUID()}>- &#x25;</strong> Claimed Deals
                    <AdvertiserOnlyPillComponent customText="Advertiser Only" />
                    <div className="non-advertiser-subtitle">
                      <DealsModal ctaText="Learn how deals can help your business" returnLinkOnly name={name} phone={phoneNumber} />
                    </div>
                  </span>
                </Otherwise>
              </Choose>
            </li>
            <li>
              <Link
                id="learn-about-calculations"
                to={urlCatalog.office.performanceFAQ(serviceProviderId)}
              >
                How does Angi Ads calculate these customer contacts?
              </Link>
            </li>
          </ul>
        </Col>
      </Row>
    </div>
  );
};

export default (CustomerContactsChartComponent);



// WEBPACK FOOTER //
// ./src/modules/home/browser/components/CustomerContactsChart.js